import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { AuthContext } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ExternalLink } from 'lucide-react';
import { PoliticalOrg } from '../types/PoliticalOrg';
import { ANALYTICS_EVENTS, trackEvent } from '../utils/analytics';

interface Campaign {
  id: string;
  title: string;
  description: string;
  image?: string;
  source_url?: string;
}

interface LocationState {
  campaign?: Campaign;
  selectedOrg?: PoliticalOrg;
  amount?: number;
  comment?: string;
}

type DonationProvider = 'ACTBLUE' | 'WINRED' | 'OTHER';

type PoliticalOrgRedirect = PoliticalOrg & {
  donation_provider?: DonationProvider | null;
  donation_url?: string | null;
};

const SITE_URL = 'https://ballotr.com';
const MIN_CONTRIBUTION_AMOUNT = 1;
const MAX_CONTRIBUTION_AMOUNT = 1000;

const PENDING_RETURN_HOME_KEY = 'ballotr_pending_return_home';
const NEXT_TOAST_KEY = 'ballotr_next_toast';
const PENDING_REDIRECT_TOKEN_KEY = 'ballotr_pending_redirect_token';

const isValidAction = (value: string | null): value is 'support' | 'oppose' => {
  return value === 'support' || value === 'oppose';
};

const isOrgAvailableForContribution = (org: PoliticalOrgRedirect | null): boolean => {
  return Boolean(org?.is_active && org?.is_verified && org?.donation_url);
};

const ContributionDetails: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const campaignId = searchParams.get('campaignId');
  const rawAction = searchParams.get('action');
  const action = isValidAction(rawAction) ? rawAction : null;
  const orgId = searchParams.get('orgId');

  const state = (location.state || {}) as LocationState;

  const [campaign, setCampaign] = useState<Campaign | null>(state.campaign || null);
  const [selectedOrg, setSelectedOrg] = useState<PoliticalOrgRedirect | null>(
    (state.selectedOrg as PoliticalOrgRedirect) || null
  );

  const [amount] = useState<number | null>(state.amount ?? null);
  const [comment] = useState<string>(state.comment ?? '');

  const [profileCountry, setProfileCountry] = useState<string | null>(null);
  const [profileZip, setProfileZip] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingRedirectToken, setPendingRedirectToken] = useState<string | null>(null);
  const [awaitingReturnConfirmation, setAwaitingReturnConfirmation] = useState(false);
  const [isResolvingReturn, setIsResolvingReturn] = useState(false);

  const viewTrackedRef = useRef(false);
  const orgPresentTrackedRef = useRef(false);
  const amountTrackedRef = useRef(false);
  const commentTrackedRef = useRef(false);

  const providerLabel = useMemo(() => {
    const p = selectedOrg?.donation_provider;
    if (p === 'ACTBLUE') return 'ActBlue';
    if (p === 'WINRED') return 'WinRed';

    const orgName = selectedOrg?.name?.trim();
    if (orgName) return `${orgName} Contribution`;

    return 'Donation site';
  }, [selectedOrg?.donation_provider, selectedOrg?.name]);

  const continueButtonLabel = useMemo(() => {
    const p = selectedOrg?.donation_provider;
    if (p === 'ACTBLUE') return 'Continue to ActBlue';
    if (p === 'WINRED') return 'Continue to WinRed';

    const orgName = selectedOrg?.name?.trim();
    if (orgName) return `Continue to ${orgName} Contribution`;

    return 'Continue to donation site';
  }, [selectedOrg?.donation_provider, selectedOrg?.name]);

  const setNextToastAndPendingReturn = (message: string, redirectToken: string) => {
    try {
      sessionStorage.setItem(NEXT_TOAST_KEY, message);
      sessionStorage.setItem(PENDING_RETURN_HOME_KEY, '1');
      sessionStorage.setItem(PENDING_REDIRECT_TOKEN_KEY, redirectToken);
    } catch {
      // Ignore storage errors.
    }
  };

  const clearPendingReturnState = () => {
    try {
      sessionStorage.removeItem(PENDING_RETURN_HOME_KEY);
      sessionStorage.removeItem(NEXT_TOAST_KEY);
      sessionStorage.removeItem(PENDING_REDIRECT_TOKEN_KEY);
    } catch {
      // Ignore storage errors.
    }
  };

  const buildCampaignReturnPath = (returnStatus: 'completed' | 'canceled') => {
    if (!campaignId) return '/';
    return `/campaign/${campaignId}?return_status=${returnStatus}`;
  };

  const maybeHandleReturn = useCallback(() => {
    try {
      const pending = sessionStorage.getItem(PENDING_RETURN_HOME_KEY);
      const redirectToken = sessionStorage.getItem(PENDING_REDIRECT_TOKEN_KEY);

      if (!pending || !redirectToken) return;
      if (document.visibilityState !== 'visible') return;

      setPendingRedirectToken(redirectToken);
      setAwaitingReturnConfirmation(true);

      trackEvent(ANALYTICS_EVENTS.RETURN_TO_BALLOTR_DETECTED, {
        campaign_id: campaignId,
        org_id: orgId,
        intent_type: action,
        redirect_token_present: true,
      });
    } catch {
      // Ignore return detection errors.
    }
  }, [campaignId, orgId, action]);

  useEffect(() => {
    window.addEventListener('focus', maybeHandleReturn);
    document.addEventListener('visibilitychange', maybeHandleReturn);

    maybeHandleReturn();

    return () => {
      window.removeEventListener('focus', maybeHandleReturn);
      document.removeEventListener('visibilitychange', maybeHandleReturn);
    };
  }, [maybeHandleReturn]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('country, postal_code')
        .eq('id', user.id)
        .single();

      if (error) return;

      setProfileCountry(data?.country ?? null);
      setProfileZip(data?.postal_code ?? null);
    };

    loadProfile();
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      if (!campaignId || !action || !orgId) {
        toast.error('Missing contribution context.');
        navigate('/');
        return;
      }

      try {
        if (selectedOrg && !isOrgAvailableForContribution(selectedOrg)) {
          toast.error('This organization is not available for contributions yet.');
          navigate('/');
          return;
        }

        if (!selectedOrg) {
          const { data: orgData, error: orgError } = await supabase
            .from('political_orgs')
            .select('*')
            .eq('id', orgId)
            .single();

          if (orgError || !orgData) {
            throw orgError || new Error('Org not found');
          }

          const org = orgData as PoliticalOrgRedirect;

          if (!isOrgAvailableForContribution(org)) {
            toast.error('This organization is not available for contributions yet.');
            navigate('/');
            return;
          }

          setSelectedOrg(org);
        }

        if (!campaign) {
          const { data: campData, error: campError } = await supabase
            .from('campaigns')
            .select('*')
            .eq('id', campaignId)
            .single();

          if (campError || !campData) {
            throw campError || new Error('Campaign not found');
          }

          setCampaign(campData as Campaign);
        }
      } catch (err) {
        console.error('[ContributionDetails] fetch error', err);
        toast.error('Failed to load contribution details.');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [campaignId, action, orgId, selectedOrg, campaign, navigate]);

  useEffect(() => {
    if (loading || !campaignId || !orgId || !action || viewTrackedRef.current) return;

    trackEvent(ANALYTICS_EVENTS.CONTRIBUTION_DETAILS_VIEWED, {
      campaign_id: campaignId,
      org_id: orgId,
      intent_type: action,
      has_campaign_state: Boolean(campaign),
      has_org_state: Boolean(selectedOrg),
      amount_present: Boolean(amount),
      comment_present: Boolean(comment.trim()),
    });

    viewTrackedRef.current = true;
  }, [loading, campaignId, orgId, action, campaign, selectedOrg, amount, comment]);

  useEffect(() => {
    if (!selectedOrg || orgPresentTrackedRef.current) return;

    trackEvent(ANALYTICS_EVENTS.CONTRIBUTION_ORG_PRESENT, {
      org_id: selectedOrg.id,
      org_name: selectedOrg.name,
      donation_provider: selectedOrg.donation_provider ?? 'OTHER',
      has_donation_url: Boolean(selectedOrg.donation_url),
      org_level: selectedOrg.level,
      state_code: selectedOrg.state_code,
      party_family: selectedOrg.party_family,
      is_active: selectedOrg.is_active,
      is_verified: selectedOrg.is_verified,
    });

    orgPresentTrackedRef.current = true;
  }, [selectedOrg]);

  useEffect(() => {
    if (amount == null || amountTrackedRef.current) return;

    trackEvent(ANALYTICS_EVENTS.CONTRIBUTION_AMOUNT_ENTERED, {
      campaign_id: campaignId,
      org_id: orgId,
      intent_type: action,
      amount,
      amount_cents: Math.round(amount * 100),
    });

    amountTrackedRef.current = true;
  }, [amount, campaignId, orgId, action]);

  useEffect(() => {
    if (!comment.trim() || commentTrackedRef.current) return;

    trackEvent(ANALYTICS_EVENTS.CONTRIBUTION_COMMENT_ENTERED, {
      campaign_id: campaignId,
      org_id: orgId,
      intent_type: action,
      comment_length: comment.trim().length,
    });

    commentTrackedRef.current = true;
  }, [comment, campaignId, orgId, action]);

  const buildProviderUrl = (baseUrl: string, token: string): string => {
    const url = new URL(baseUrl);
    const refcode = `ballotr_${campaignId ?? 'unknown'}_${token}`;
    const isLibertarianParty =
      selectedOrg?.donation_provider === 'OTHER' &&
      selectedOrg?.name?.trim().toLowerCase() === 'libertarian party';

    url.searchParams.set('refcode', refcode);

    if (amount && amount > 0) {
      if (isLibertarianParty) {
        url.searchParams.set('Donation', String(amount));
      } else {
        url.searchParams.set('amount', String(amount));
      }
    }

    if (action) url.searchParams.set('intent', action);
    if (orgId) url.searchParams.set('org', orgId);

    return url.toString();
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please sign in to contribute.');
      navigate('/auth');
      return;
    }

    if (!campaignId || !action || !orgId || !selectedOrg) {
      toast.error('Missing contribution context.');
      return;
    }

    if (!isOrgAvailableForContribution(selectedOrg)) {
      toast.error('This organization is not available for contributions yet.');
      return;
    }

    if (!amount || amount < MIN_CONTRIBUTION_AMOUNT || amount > MAX_CONTRIBUTION_AMOUNT) {
      toast.error(
        `Contribution amount must be between $${MIN_CONTRIBUTION_AMOUNT} and $${MAX_CONTRIBUTION_AMOUNT}.`
      );
      navigate(`/contribute?campaignId=${campaignId}&action=${action}&orgId=${orgId}`, {
        state: { selectedOrg, campaign, amount, comment },
      });
      return;
    }

    if ((profileCountry && profileCountry !== 'United States') || !profileZip) {
      toast.error(
        'To contribute, please confirm you are in the United States and add a ZIP code in your profile.'
      );
      navigate('/profile');
      return;
    }

    const donationUrl = selectedOrg.donation_url;
    const provider = (selectedOrg.donation_provider ?? 'OTHER') as DonationProvider;

    if (!donationUrl) {
      toast.error('This organization is not configured for external donations yet.');
      return;
    }

    const redirectToken =
      globalThis.crypto && 'randomUUID' in globalThis.crypto
        ? (globalThis.crypto as Crypto).randomUUID()
        : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

    const providerUrl = buildProviderUrl(donationUrl, redirectToken);

    trackEvent(ANALYTICS_EVENTS.REDIRECT_SUBMIT_ATTEMPTED, {
      campaign_id: campaignId,
      org_id: orgId,
      intent_type: action,
      amount,
      amount_cents: Math.round(amount * 100),
      provider,
      has_comment: Boolean(comment.trim()),
    });

    setIsSubmitting(true);

    try {
      const { data: insertedEvent, error: insertError } = await supabase
        .from('donation_redirect_events')
        .insert([
          {
            user_id: user.id,
            campaign_id: campaignId,
            org_id: orgId,
            intent_type: action,
            amount_cents: Math.round(amount * 100),
            comment: comment || null,
            provider,
            provider_url: providerUrl,
            redirect_token: redirectToken,
            status: 'redirected',
          },
        ])
        .select('id')
        .single();

      if (insertError) throw insertError;

      if (comment.trim()) {
        const { error: commentInsertError } = await supabase.from('campaign_comments').insert([
          {
            campaign_id: campaignId,
            user_id: user.id,
            contribution_event_id: insertedEvent.id,
            intent_type: action,
            body: comment.trim(),
          },
        ]);

        if (commentInsertError) throw commentInsertError;
      }

      trackEvent(ANALYTICS_EVENTS.REDIRECT_EVENT_CREATED, {
        campaign_id: campaignId,
        org_id: orgId,
        intent_type: action,
        amount,
        amount_cents: Math.round(amount * 100),
        provider,
        org_name: selectedOrg.name,
        org_level: selectedOrg.level,
        state_code: selectedOrg.state_code,
        party_family: selectedOrg.party_family,
      });

      const orgName = selectedOrg?.name?.trim() || 'this organization';
      setNextToastAndPendingReturn(
        `Your contribution intent to ${orgName} was recorded.`,
        redirectToken
      );

      trackEvent(ANALYTICS_EVENTS.REDIRECT_OPENED, {
        campaign_id: campaignId,
        org_id: orgId,
        intent_type: action,
        provider,
        org_name: selectedOrg.name,
        org_level: selectedOrg.level,
        state_code: selectedOrg.state_code,
        party_family: selectedOrg.party_family,
      });

      const newTab = window.open(providerUrl, '_blank', 'noopener,noreferrer');

      if (!newTab) {
        toast.error(
          'Popup blocked. Please allow popups for Ballotr to open the donation site in a new tab.'
        );
        return;
      }
    } catch (err: any) {
      console.error('[ContributionDetails] redirect log error', err);

      trackEvent(ANALYTICS_EVENTS.REDIRECT_EVENT_FAILED, {
        campaign_id: campaignId,
        org_id: orgId,
        intent_type: action,
        error_message: err?.message || 'Failed to start donation redirect.',
      });

      toast.error(err?.message || 'Failed to start donation redirect.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelfReportedCompletion = async () => {
    if (!user || !pendingRedirectToken) return;

    trackEvent(ANALYTICS_EVENTS.SELF_REPORTED_COMPLETION_CLICKED, {
      campaign_id: campaignId,
      org_id: orgId,
      intent_type: action,
    });

    setIsResolvingReturn(true);

    try {
      const { error } = await supabase
        .from('donation_redirect_events')
        .update({
          status: 'self_reported_completed',
          completed_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .eq('redirect_token', pendingRedirectToken);

      if (error) throw error;

      trackEvent(ANALYTICS_EVENTS.SELF_REPORTED_COMPLETION_SUCCEEDED, {
        campaign_id: campaignId,
        org_id: orgId,
        intent_type: action,
      });

      clearPendingReturnState();
      setAwaitingReturnConfirmation(false);
      setPendingRedirectToken(null);

      navigate(buildCampaignReturnPath('completed'), {
        state: campaign ? { campaign } : undefined,
      });
    } catch (err: any) {
      console.error('[ContributionDetails] self-report completion error', err);

      trackEvent(ANALYTICS_EVENTS.SELF_REPORTED_COMPLETION_FAILED, {
        campaign_id: campaignId,
        org_id: orgId,
        intent_type: action,
        error_message: err?.message || 'Failed to confirm contribution.',
      });

      toast.error(err?.message || 'Failed to confirm contribution.');
    } finally {
      setIsResolvingReturn(false);
    }
  };

  const handleRedirectCanceled = async () => {
    if (!user || !pendingRedirectToken) return;

    setIsResolvingReturn(true);

    try {
      const { error } = await supabase
        .from('donation_redirect_events')
        .update({
          status: 'canceled',
        })
        .eq('user_id', user.id)
        .eq('redirect_token', pendingRedirectToken);

      if (error) throw error;

      trackEvent(ANALYTICS_EVENTS.REDIRECT_CANCELED, {
        campaign_id: campaignId,
        org_id: orgId,
        intent_type: action,
      });

      clearPendingReturnState();
      setAwaitingReturnConfirmation(false);
      setPendingRedirectToken(null);

      navigate(buildCampaignReturnPath('canceled'), {
        state: campaign ? { campaign } : undefined,
      });
    } catch (err: any) {
      console.error('[ContributionDetails] redirect cancel error', err);
      toast.error(err?.message || 'Failed to update redirect status.');
    } finally {
      setIsResolvingReturn(false);
    }
  };

  if (!campaignId || !action || !orgId || loading) {
    return (
      <>
        <Helmet>
          <title>Contribution Redirect | Ballotr</title>
          <meta
            name="description"
            content="Complete the Ballotr contribution redirect flow in a private session."
          />
          <meta name="robots" content="noindex,nofollow" />
          <link rel="canonical" href={`${SITE_URL}/contribute/details`} />
        </Helmet>

        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-xl font-semibold mb-2">Loading contribution details...</h1>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contribution Redirect | Ballotr</title>
        <meta
          name="description"
          content="Complete the Ballotr contribution redirect flow in a private session."
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={`${SITE_URL}/contribute/details`} />
      </Helmet>

      <main className="bg-secondary min-h-screen py-8 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-lg shadow-md p-6 space-y-6">
          <div>
            {campaign && (
              <div className="relative bg-gray-100 p-4 rounded-md">
                <h2
                  className={`text-md font-semibold mb-1 ${
                    action === 'support' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  You are {action === 'support' ? 'Supporting' : 'Opposing'}:
                </h2>

                {campaign.source_url ? (
                  <a
                    href={campaign.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-black font-bold text-lg hover:underline"
                  >
                    {campaign.title}
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                ) : (
                  <p className="text-black font-bold text-lg">{campaign.title}</p>
                )}

                <p className="text-sm text-gray-600 mt-1">{campaign.description}</p>
              </div>
            )}

            {selectedOrg && amount && (
              <div className="mt-4 p-4 border rounded-md bg-gray-50 space-y-2">
                <p className="text-sm text-gray-700">
                  You’re contributing <span className="font-semibold">${amount}</span> to{' '}
                  <span className="font-semibold">{selectedOrg.name}</span>.
                </p>

                <p className="text-xs text-gray-600">
                  You’ll be redirected to <span className="font-medium">{providerLabel}</span> to
                  complete your contribution in a new tab. Return here when you’re done.
                </p>
              </div>
            )}

            {awaitingReturnConfirmation && (
              <div className="mt-4 p-4 border rounded-md bg-blue-50 space-y-3">
                <p className="text-sm font-medium text-slate-900">
                  Welcome back. Were you able to complete your contribution?
                </p>
                <p className="text-xs text-slate-600">
                  This helps Ballotr measure whether donation redirects are working correctly.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleSelfReportedCompletion}
                    disabled={isResolvingReturn}
                    className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-600 transition disabled:opacity-60"
                  >
                    {isResolvingReturn ? 'Saving...' : 'Yes, I completed it'}
                  </button>

                  <button
                    type="button"
                    onClick={handleRedirectCanceled}
                    disabled={isResolvingReturn}
                    className="border border-slate-300 text-slate-700 px-4 py-2 rounded-md hover:bg-slate-50 transition disabled:opacity-60"
                  >
                    I canceled / did not complete
                  </button>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleContinue} className="space-y-4">
            <div className="flex justify-between items-center pt-4 border-t">
              <button
                type="button"
                className="text-sm text-blue-600"
                onClick={() =>
                  navigate(`/contribute?campaignId=${campaignId}&action=${action}&orgId=${orgId}`, {
                    state: { selectedOrg, campaign, amount, comment },
                  })
                }
              >
                ← Back
              </button>

              <button
                type="submit"
                disabled={isSubmitting || awaitingReturnConfirmation}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-600 transition disabled:opacity-60"
              >
                {isSubmitting ? 'Opening new tab...' : continueButtonLabel}
              </button>
            </div>
          </form>
        </div>
      </main>
    </>
  );
};

export default ContributionDetails;